import type { ICalendarEventListItem } from '@/types/shared-types'
import type { Block, Document, Inline, Text } from '@contentful/rich-text-types'

type TRichTextNode = Document | Block | Inline | Text

const toIcsDateTime = (date: string) =>
  new Date(date).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

// keeps the literal CMS calendar date — parsing via Date() could shift all-day events across timezones
const toIcsDate = (date: string) => date.slice(0, 10).replace(/-/g, '')

const escapeIcsText = (text: string) =>
  text.replace(/[\\;,]/g, char => `\\${char}`).replace(/\r?\n/g, '\\n')

const richTextToPlainText = (node: TRichTextNode | null): string => {
  if (!node) {
    return ''
  }

  // only text nodes have value — containers recurse through content
  if ('value' in node) {
    return node.value
  }

  return node.content.map(child => richTextToPlainText(child)).join(' ')
}

const createIcsFileName = (title: string) => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return `${slug}.ics`
}

const createIcsFileContent = (event: ICalendarEventListItem) => {
  const summary = escapeIcsText(event.title)
  const description = escapeIcsText(richTextToPlainText(event.content).replace(/\s+/g, ' ').trim())

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BENEFIT SYSTEMS//PL',
    'BEGIN:VEVENT',
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    `DTSTAMP:${toIcsDateTime(event.date)}`,
    `DTSTART;VALUE=DATE:${toIcsDate(event.date)}`,
    'SEQUENCE:0',
    `UID:${event.id}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${summary}`,
    'TRIGGER:-PT12H',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n')
}

const downloadIcsFile = (fileName: string, content: string) => {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  link.click()

  URL.revokeObjectURL(url)
}

export const useAddToCalendar = () => {
  const addToCalendar = (event: ICalendarEventListItem) => {
    downloadIcsFile(createIcsFileName(event.title), createIcsFileContent(event))
  }

  return {
    addToCalendar,
  }
}
